import axios, { type AxiosResponse } from 'axios';
import { buildRegisterUrl, usesStandaloneRegisterApi } from '../config/signupApiUrls';

export type RegisterFormPayload = {
    name: string;
    email: string;
    phone: string;
    password: string;
    project_type: string;
    company_logo: File | null;
};

const JSON_REGISTER_PROJECT_TYPES = new Set(['hr_management']);

function isHtmlRegisterResponse(response: AxiosResponse): boolean {
    const contentType = String(response.headers['content-type'] ?? '').toLowerCase();
    if (contentType.includes('text/html')) return true;
    const data = response.data;
    if (typeof data === 'string' && data.trim().startsWith('<')) return true;
    return false;
}

function assertJsonRegisterResponse(response: AxiosResponse): void {
    if (isHtmlRegisterResponse(response)) {
        throw new Error('INVALID_API_RESPONSE');
    }
}

export async function postRegister(
    projectType: string,
    data: RegisterFormPayload,
    options: { social?: string | null; verifiedPlanId?: string | null },
): Promise<AxiosResponse> {
    const url = buildRegisterUrl(projectType, options.social);

    if (JSON_REGISTER_PROJECT_TYPES.has(projectType)) {
        const response = await axios.post(
            url,
            {
                name: data.name,
                email: data.email,
                phone: data.phone,
                password: data.password,
                password_confirmation: data.password,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            },
        );
        assertJsonRegisterResponse(response);
        await notifyControlPlaneRegistrationIntake(data, projectType, options.verifiedPlanId);
        return response;
    }

    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    formData.append('password', data.password);
    if (data.company_logo) {
        formData.append('company_logo', data.company_logo);
    }
    formData.append('project_type', data.project_type ?? '');

    if (options.verifiedPlanId && usesStandaloneRegisterApi(projectType)) {
        formData.append('plan_id', options.verifiedPlanId);
    }

    const response = await axios.post(url, formData, {
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    });
    assertJsonRegisterResponse(response);
    await notifyControlPlaneRegistrationIntake(data, projectType, options.verifiedPlanId);
    return response;
}

/** Best-effort: list signup in control-plane registrations queue (production). */
async function notifyControlPlaneRegistrationIntake(
    data: RegisterFormPayload,
    projectType: string,
    planId?: string | null,
): Promise<void> {
    const intakeUrl = import.meta.env.VITE_CONTROL_PLANE_INTAKE_URL as string | undefined;
    if (!intakeUrl) return;
    try {
        await axios.post(
            intakeUrl,
            {
                source: 'mrnware-website',
                company: { name: data.name },
                contact: { first_name: data.name, email: data.email, phone: data.phone },
                requested_products: [{ product: projectType, plan_id: planId ?? null }],
            },
            { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } },
        );
    } catch {
        // Non-blocking: registration UX must not fail if intake is down.
    }
}

/**
 * Standalone register sometimes creates the user then throws when linking plan/subscription (AuthController ~113).
 * User is already registered — treat as success until the API is fixed.
 */
export function isRegisterCompletedDespiteBackendError(err: unknown): boolean {
    if (!axios.isAxiosError(err)) return false;
    const payload = err.response?.data;
    const message =
        payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
            ? payload.message
            : '';
    return message.includes('Attempt to read property') && message.includes('on null');
}

export type RegisterErrorMessages = {
    fallback: string;
    invalidApi: string;
    emailAlreadyRegistered: string;
};

function extractApiMessage(payload: unknown): string {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (typeof payload !== 'object') return '';
    if ('message' in payload && typeof payload.message === 'string') {
        return payload.message;
    }
    return '';
}

function extractValidationMessages(payload: unknown): string[] {
    if (!payload || typeof payload !== 'object' || !('errors' in payload)) return [];
    const errors = payload.errors;
    if (!errors || typeof errors !== 'object') return [];
    return Object.values(errors as Record<string, string[]>).flat();
}

/** Duplicate key / unique constraint on email (e.g. customers.customers_email_unique). */
export function isDuplicateEmailRegisterError(message: string): boolean {
    const m = message.toLowerCase();
    return (
        m.includes('duplicate entry') ||
        m.includes('integrity constraint violation') ||
        m.includes('sqlstate[23000]') ||
        m.includes('email_unique') ||
        m.includes('customers_email_unique') ||
        (m.includes('already been taken') && m.includes('email'))
    );
}

export function getRegisterErrorMessage(err: unknown, messages: RegisterErrorMessages): string {
    if (err instanceof Error && err.message === 'INVALID_API_RESPONSE') {
        return messages.invalidApi;
    }
    if (axios.isAxiosError(err)) {
        const payload = err.response?.data;
        const apiMessage = extractApiMessage(payload);
        const validationMessages = extractValidationMessages(payload);
        const combined = [apiMessage, ...validationMessages].filter(Boolean).join(' ');

        if (combined && isDuplicateEmailRegisterError(combined)) {
            return messages.emailAlreadyRegistered;
        }

        if (validationMessages.length > 0) {
            const emailError = validationMessages.find((msg) => isDuplicateEmailRegisterError(msg));
            if (emailError) return messages.emailAlreadyRegistered;
            return validationMessages[0];
        }

        if (apiMessage) {
            if (isDuplicateEmailRegisterError(apiMessage)) {
                return messages.emailAlreadyRegistered;
            }
            return apiMessage;
        }

        if (!err.response) {
            return messages.invalidApi;
        }
    }
    return messages.fallback;
}
